import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import { HERO_VIDEO } from "@/data/content";

export const Showreel = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [started, setStarted] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.muted = muted;
      v.play();
      setPlaying(true);
      setStarted(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section id="showreel" className="relative bg-brand-dark py-24 lg:py-36" data-testid="showreel-section">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="font-outfit text-xs tracking-[0.3em] uppercase text-brand-red font-bold mb-4">— Showreel</p>
            <h2 className="font-anton uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight max-w-2xl">
              Guarda il<br />nostro lavoro
            </h2>
          </div>
          <p className="font-manrope font-light text-neutral-400 max-w-sm lg:text-right">
            60 secondi di pellicole, dettagli e trasformazioni. Alza il volume e premi play.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          onClick={toggle}
          data-testid="showreel-player"
          className="group relative aspect-video w-full overflow-hidden rounded-xl border border-brand-border cursor-pointer"
        >
          <video
            ref={videoRef}
            src={HERO_VIDEO}
            preload="metadata"
            playsInline
            onEnded={() => setPlaying(false)}
            data-testid="showreel-video"
            className="w-full h-full object-cover"
          />

          {!started && <div className="absolute inset-0 bg-brand-dark/40 transition-opacity duration-500" />}

          {/* play / pause overlay */}
          <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
            <span
              data-testid="showreel-play-btn"
              className="h-20 w-20 lg:h-28 lg:w-28 rounded-full bg-brand-red flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-2xl"
            >
              {playing ? (
                <Pause size={38} weight="fill" className="text-white" />
              ) : (
                <Play size={38} weight="fill" className="text-white ml-1" />
              )}
            </span>
          </div>

          {/* mute toggle */}
          {started && (
            <button
              onClick={toggleMute}
              data-testid="showreel-mute-btn"
              aria-label={muted ? "Attiva audio" : "Disattiva audio"}
              className="absolute bottom-5 right-5 h-12 w-12 rounded-full bg-brand-dark/70 backdrop-blur-md border border-brand-border flex items-center justify-center text-white hover:bg-brand-red hover:border-brand-red transition-colors duration-300"
            >
              {muted ? <SpeakerSlash size={22} weight="bold" /> : <SpeakerHigh size={22} weight="bold" />}
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};
